// Save/load/migrate + localStorage wrapper (incognito-safe). (TECHNICAL_DESIGN.md §3.)
import { GROVE_TOTEM_COSTS, PLAYER_HP } from './const';
import { makeCraftedGear } from './items';
import { SAVE_VERSION, type SaveData } from './types';

const KEY = 'wildergrove.save';
const SOFT_LIMIT = 512_000; // UTF-16 units (~1 MB)
const HARD_LIMIT = 1_500_000; // ~3 MB

export function freshSave(seed: number, spawnX: number, spawnY: number, nowMs: number): SaveData {
  return {
    version: SAVE_VERSION,
    seed: seed >>> 0,
    createdAtMs: nowMs,
    lastSeenMs: nowMs,
    clock: { gameS: 0 },
    player: {
      x: spawnX,
      y: spawnY,
      hp: PLAYER_HP,
      gold: 0,
      name: '',
      palette: { body: 0, hair: 0, outfit: 0 },
    },
    home: { x: spawnX, y: spawnY },
    skills: { wood: 0, mine: 0, combat: 0, craft: 0 },
    tools: { axe: 0, pickaxe: 0 },
    weapons: { melee: 0, bow: 0 },
    armor: 0,
    inventory: {},
    buffs: { food: [], warmedUntilGameS: 0 },
    world: { harvested: {}, placed: [] },
    embers: { lastCollectMs: nowMs },
    gear: [],
    equipped: {},
    relics: [],
    equippedRelics: [null, null],
    dungeon: { deepest: 0, checkpoints: [], pity: 0, bests: {}, tier: 1 },
    satchel: { gold: 0, items: {}, gear: [] },
    totems: [false, false, false],
    blessing: { lastAccrualDayUTC: Math.floor(nowMs / 86_400_000), banked: 0, claimedDayUTC: -1, activeId: null },
    sanctuary: 0,
    codex: { mobs: [], recipes: [], relics: [] },
    grove: { fed: 0, stage: 0 },
  };
}

/** Cumulative grove relics needed to reach each stage (prefix sums of GROVE_TOTEM_COSTS). */
export function groveThreshold(stage: number): number {
  let sum = 0;
  for (let i = 0; i < stage; i++) sum += GROVE_TOTEM_COSTS[i] ?? Infinity;
  return sum;
}

/** Apply the migration chain to any prior/unknown blob → current shape. */
export function migrate(raw: unknown): SaveData | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<SaveData> & { version?: number };
  if (typeof s.version !== 'number' || typeof s.seed !== 'number') return null;

  // v1 → v2: inventory / buffs / harvested introduced.
  if (s.version < 2) {
    s.inventory ??= {};
    s.buffs ??= { food: [], warmedUntilGameS: 0 };
    s.world ??= { harvested: {}, placed: [] };
    s.version = 2;
  }
  // v2 → v3: crafting tiers, placeables, warmed, embers.
  if (s.version < 3) {
    s.tools ??= { axe: 0, pickaxe: 0 };
    s.weapons ??= { melee: 0, bow: 0 };
    s.armor ??= 0;
    s.buffs ??= { food: [], warmedUntilGameS: 0 };
    s.buffs.warmedUntilGameS ??= 0;
    s.world ??= { harvested: {}, placed: [] };
    s.world.placed ??= [];
    // anchor embers to lastSeen (NOT epoch) so migration never pays a 24h windfall
    s.embers ??= { lastCollectMs: s.lastSeenMs ?? s.createdAtMs ?? 0 };
    s.version = 3;
  }
  // v3 → v4: dungeons, satchel, totems.
  if (s.version < 4) {
    s.dungeon ??= { deepest: 0, checkpoints: [], pity: 0, bests: {}, tier: 1 };
    s.satchel ??= { gold: 0, items: {}, gear: [] };
    s.totems ??= [false, false, false];
    s.version = 4;
  }
  // v4 → v5: gear/equipment/pity. Crafted tiers stay as the baseline (no conversion).
  if (s.version < 5) {
    s.gear ??= [];
    s.equipped ??= {};
    if (s.satchel) s.satchel.gear ??= [];
    if (s.dungeon) s.dungeon.pity ??= 0;
    s.version = 5;
  }
  // v5 → v6: empower/motif on gear, blessing, farming (bests/tier).
  if (s.version < 6) {
    const fixGear = (arr?: { empower?: unknown }[]) => arr?.forEach((g) => { g.empower ??= { level: 0, bumps: {} }; });
    fixGear(s.gear as never);
    fixGear(s.satchel?.gear as never);
    if (s.dungeon) { s.dungeon.bests ??= {}; s.dungeon.tier ??= 1; }
    s.blessing ??= { lastAccrualDayUTC: Math.floor((s.lastSeenMs ?? 0) / 86_400_000), banked: 0, claimedDayUTC: -1, activeId: null };
    s.version = 6;
  }
  // v6 → v7: upgradeable home sanctuary.
  if (s.version < 7) {
    s.sanctuary ??= 0;
    s.version = 7;
  }
  // v7 → v8: the wanderer's chosen name (older saves keep the "Hero" fallback).
  if (s.version < 8) {
    if (s.player) s.player.name ??= '';
    s.version = 8;
  }
  // v8 → v9: six equipment slots + dungeon relics; crafted tiers become equipped gear.
  if (s.version < 9) {
    s.relics ??= [];
    s.equippedRelics ??= [null, null];
    s.gear ??= [];
    s.equipped ??= {};
    // the old single "trinket" slot is now "necklace"
    const eq = s.equipped as Record<string, string | undefined>;
    if (eq.trinket && !eq.necklace) eq.necklace = eq.trinket;
    delete eq.trinket;
    for (const g of [...s.gear, ...(s.satchel?.gear ?? [])]) if ((g as { slot: string }).slot === 'trinket') (g as { slot: string }).slot = 'necklace';
    // convert crafted weapon/bow/armor tiers into equipped crafted gear (one-time)
    const addCrafted = (recipeId: string, uid: string) => {
      if (s.gear!.some((g) => g.crafted === recipeId)) return;
      const base = makeCraftedGear(recipeId);
      if (!base) return;
      s.gear!.push({ ...base, uid });
      if (!s.equipped![base.slot]) s.equipped![base.slot] = uid;
    };
    const w = s.weapons?.melee ?? 0, b = s.weapons?.bow ?? 0, a = s.armor ?? 0;
    if (w >= 2) addCrafted('iron_sword', 'c_wpn'); else if (w >= 1) addCrafted('stone_sword', 'c_wpn');
    if (b >= 2) addCrafted('iron_bow', 'c_bow'); else if (b >= 1) addCrafted('wood_bow', 'c_bow');
    if (a >= 2) addCrafted('iron_armor', 'c_arm'); else if (a >= 1) addCrafted('leather_armor', 'c_arm');
    s.version = 9;
  }
  // v9 → v10: collection log. Seed with endowed progress from what the player already owns —
  // crafted recipes and found relic effects — so returning players open a partly-filled codex.
  if (s.version < 10) {
    const recipes = [...new Set((s.gear ?? []).map((g) => (g as { crafted?: string }).crafted).filter(Boolean) as string[])];
    const relics = [...new Set((s.relics ?? []).map((r) => (r as { effect: string }).effect))];
    s.codex ??= { mobs: [], recipes, relics };
    s.version = 10;
  }
  // v10 → v11: the grove restoration arc. Seed from the old 3-totem quest so no progress is lost —
  // each previously-lit totem counts as one restored stage.
  if (s.version < 11) {
    const lit = (s.totems ?? []).filter(Boolean).length;
    s.grove ??= { fed: groveThreshold(lit), stage: lit };
    s.version = 11;
  }

  // Defensive tail — a partial blob never crashes the game.
  s.clock ??= { gameS: 0 };
  if (s.player) s.player.name ??= '';
  s.home ??= { x: s.player?.x ?? 0, y: s.player?.y ?? 0 };
  s.skills ??= { wood: 0, mine: 0, combat: 0, craft: 0 };
  s.tools ??= { axe: 0, pickaxe: 0 };
  s.weapons ??= { melee: 0, bow: 0 };
  s.armor ??= 0;
  s.inventory ??= {};
  s.buffs ??= { food: [], warmedUntilGameS: 0 };
  s.buffs.food ??= [];
  s.buffs.warmedUntilGameS ??= 0;
  s.world ??= { harvested: {}, placed: [] };
  s.world.harvested ??= {};
  s.world.placed ??= [];
  s.embers ??= { lastCollectMs: s.lastSeenMs ?? 0 };
  s.gear ??= [];
  s.equipped ??= {};
  s.relics ??= [];
  s.equippedRelics ??= [null, null];
  s.dungeon ??= { deepest: 0, checkpoints: [], pity: 0, bests: {}, tier: 1 };
  s.dungeon.pity ??= 0;
  s.dungeon.bests ??= {};
  s.dungeon.tier ??= 1;
  s.satchel ??= { gold: 0, items: {}, gear: [] };
  s.satchel.gear ??= [];
  s.totems ??= [false, false, false];
  s.blessing ??= { lastAccrualDayUTC: Math.floor((s.lastSeenMs ?? 0) / 86_400_000), banked: 0, claimedDayUTC: -1, activeId: null };
  s.sanctuary ??= 0;
  s.codex ??= { mobs: [], recipes: [], relics: [] };
  s.codex.mobs ??= []; s.codex.recipes ??= []; s.codex.relics ??= [];
  s.grove ??= { fed: 0, stage: 0 };
  return s as SaveData;
}

export function serialize(save: SaveData): string {
  return JSON.stringify(save);
}

export type SaveStatus = 'ok' | 'soft' | 'hard' | 'error';

export function sizeStatus(str: string): SaveStatus {
  if (str.length > HARD_LIMIT) return 'hard';
  if (str.length > SOFT_LIMIT) return 'soft';
  return 'ok';
}

export function writeSave(save: SaveData): SaveStatus {
  try {
    const str = serialize(save);
    const status = sizeStatus(str);
    if (status === 'hard') return 'hard';
    localStorage.setItem(KEY, str);
    return status;
  } catch {
    return 'error'; // incognito / quota / disabled storage — never crash
  }
}

export function readSave(): SaveData | null {
  try {
    const str = localStorage.getItem(KEY);
    if (!str) return null;
    return migrate(JSON.parse(str));
  } catch {
    return null;
  }
}

export function hasSave(): boolean {
  try {
    return localStorage.getItem(KEY) != null;
  } catch {
    return false;
  }
}

export function exportCode(save: SaveData): string {
  try {
    return btoa(unescape(encodeURIComponent(serialize(save))));
  } catch {
    return '';
  }
}

export function importCode(code: string): SaveData | null {
  try {
    return migrate(JSON.parse(decodeURIComponent(escape(atob(code)))));
  } catch {
    return null;
  }
}
